// CARO. Casa de estilo — authoritative cart pricing.
//
// The browser is NEVER authoritative for price, VAT, availability or totals.
// The Worker calls priceCart() with products loaded fresh from D1 and the
// customer-submitted quantities; everything is recomputed server-side here.

import type {
  CartLine,
  CartLineInput,
  CartRequestLine,
  Locale,
  OrderItemSnapshot,
  OrderTotals,
  Pack,
  PackLineInput,
  Product,
} from '@/types';
import { addCents, multiplyCents, roundCents } from '@/lib/money';
import { vatFromGross, resolveVatRate, DEFAULT_VAT_RATE } from '@/lib/tax';
import { reservedForProduct } from '@/lib/holds';
import { isLive } from '@/lib/catalog/availability';
import { allocatePackDiscount, packName, packPricing } from '@/lib/catalog/packs';
import { lineKey } from './line-key';
import { productSizes, sizeUnits } from './sizes';
import {
  applyFuelSurcharge,
  calculateShipping,
  findZone,
  FUEL_SURCHARGE_PERCENT,
  tierMultiplier,
  type ShippingResult,
  type ShippingZone,
} from '@/lib/shipping';

export { lineKey, productIdOfLineKey } from './line-key';
export { productSizes, sizeStockTotal, sizeUnits } from './sizes';

export type CartIssueCode =
  | 'not_found'
  | 'not_purchasable'
  | 'out_of_stock'
  | 'insufficient_stock'
  | 'enquiry_only'
  // A sized product (metadata.sizes) without a size, or with one it doesn't offer.
  | 'size_required'
  | 'invalid_size'
  // Non-blocking: a pre-order (reasortimiento) line whose stock is currently
  // short. It is still priced and enters the order; the checkout surfaces a
  // delivery-delay warning and asks the buyer to continue or remove it.
  | 'backorder';

export interface CartIssue {
  /** The product concerned; '' for an issue of a whole pack (unknown / not active). */
  productId: string;
  /** Set when the line is (a component of) a pack line: the client removes that pack line. */
  packId?: string;
  /** Set when the issue concerns one size of a sized product. */
  size?: string;
  code: CartIssueCode;
  message: string;
}

export interface PricedCart {
  lines: CartLine[];
  items: OrderItemSnapshot[];
  subtotalCents: number;
  taxCents: number;
  currency: string;
  requiresQuote: boolean;
}

export interface CartPricingResult {
  ok: boolean;
  issues: CartIssue[];
  cart: PricedCart;
}

/** The checkout sells only `active` pieces that are live at `now` (a scheduled drop is not, see isLive). */
function isPurchasable(p: Product, now: number): boolean {
  return p.status === 'active' && isLive(p, now);
}

/** "Size" label for order/email/label lines, per storefront locale (es default). */
export function sizeLabel(locale: Locale | string): string {
  return locale === 'en' ? 'Size' : locale === 'fr' ? 'Taille' : 'Talla';
}

/**
 * Line-level problems that don't depend on stock (missing product, enquiry-only,
 * not active, bad size). Returns the issue, or the product + its normalized size
 * (undefined for unsized products, which ignore any submitted size).
 */
function checkLine(
  byId: Map<string, Product>,
  input: CartLineInput,
  now: number,
):
  | { issue: CartIssue; product?: Product }
  | { issue?: undefined; product: Product; size: string | undefined } {
  const product = byId.get(input.productId);
  if (!product) {
    return {
      issue: {
        productId: input.productId,
        code: 'not_found',
        message: 'Este producto ya no está disponible.',
      },
    };
  }
  if (product.checkoutMode === 'enquiry') {
    return {
      product,
      issue: {
        productId: product.id,
        code: 'enquiry_only',
        message: 'Esta pieza se adquiere por solicitud de información.',
      },
    };
  }
  if (!isPurchasable(product, now)) {
    return {
      product,
      issue: {
        productId: product.id,
        code: 'not_purchasable',
        message: 'Este producto no está disponible actualmente.',
      },
    };
  }
  const sizes = productSizes(product);
  if (sizes.length === 0) return { product, size: undefined };
  if (!input.size) {
    return {
      product,
      issue: { productId: product.id, code: 'size_required', message: 'Elige una talla.' },
    };
  }
  if (!sizes.includes(input.size)) {
    return {
      product,
      issue: {
        productId: product.id,
        size: input.size,
        code: 'invalid_size',
        message: 'Esta talla no está disponible.',
      },
    };
  }
  return { product, size: input.size };
}

/** True for a pack line (`{packId, quantity, sizes?}`), false for a product line. */
export function isPackLine(line: CartRequestLine): line is PackLineInput {
  return typeof (line as Partial<PackLineInput>).packId === 'string';
}

/** The size chosen for `productId` in a pack line (own keys only: the record is user input). */
function packSize(line: PackLineInput, productId: string): string | undefined {
  const sizes = line.sizes;
  if (!sizes || !Object.prototype.hasOwnProperty.call(sizes, productId)) return undefined;
  const size = sizes[productId];
  return typeof size === 'string' && size ? size : undefined;
}

/**
 * One unit of pricing work: a product line, or ONE component of a pack line
 * (expanded: quantity = units in the pack × packs, size = `sizes[productId]`).
 */
interface Entry {
  input: CartLineInput;
  /** Index of the submitted line: all the components of a pack line share it. */
  group: number;
  pack?: { pack: Pack; name: string; packs: number };
}

/**
 * Validate + price a set of line inputs against authoritative product records.
 * Returns issues per line; `ok` is true only when there are zero BLOCKING issues
 * (a `backorder` issue does not block — the line is still priced).
 *
 * Availability is checked on the SUM of all lines of a product (so two lines of
 * one product — e.g. sizes S and M — can't each claim the full stock), and for a
 * sized product with `metadata.sizeStock`, also on the sum per size against that
 * size's units. A stock issue is reported once per product / per size (and per
 * pack drawing on it, see below).
 *
 * `reservedByOthers` (checkout context): units reserved by OTHER carts, keyed by
 * line key (`lineKey`: product id, or `id#size`). When given, availability is
 * STRICT — raw stock minus those units (product: all its keys; size: its own
 * key), with no floor — so the last piece can genuinely be blocked, and a short
 * pre-order line is flagged `backorder`. When omitted (quote), availability is
 * raw stock and pre-orders stay silent (current behaviour).
 *
 * `now` (ms): a product not live yet (`metadata.publishAt` ahead, isLive) is
 * `not_purchasable`, so a scheduled drop cannot be bought before its instant
 * even with a stale cart line or a hand-made request.
 *
 * `packs` (docs/plan-packs.md): the packs referenced by pack lines, loaded fresh
 * from D1. A pack line is EXPANDED into its components (quantity = units in the
 * pack × packs, size = `sizes[productId]`), each validated by the same
 * `checkLine` and counted in the same stock sums as loose lines (a pack and a
 * loose line of one product share its units). Rules:
 * - Unknown, non-`active` or empty pack → ONE `not_purchasable` issue with
 *   `packId` and `productId: ''` (no component to point at).
 * - Every component issue carries `packId`; an `enquiry` component reads as
 *   `not_purchasable` (a pack never goes to the quote flow).
 * - All or nothing: a component with any blocking issue holds back EVERY line
 *   of that pack line (never a partial pack); a backorder warning of a pack held
 *   back that way is dropped (the pack is not sold anyway).
 * - Stock issues are de-duplicated per lot AND per owner (loose lines, or each
 *   pack), so the client learns which pack (or loose line) to remove.
 * - Demand is counted over every line-valid line, even one later held back by
 *   another component's shortage: conservative (may block a loose line that
 *   would have fit), never oversells.
 * - Price: per ONE pack, `packPricing(...).priceCents` spread over its
 *   components by `allocatePackDiscount` (Σ price × units in pack as weights);
 *   a component's `lineTotalCents` = its share × packs, so the lines of a pack
 *   sum EXACTLY to price × packs. Its `unitPriceCents` is that total ÷ quantity
 *   rounded (informational: totals, VAT and payment use `lineTotalCents`). Each
 *   line carries `packId` + `packName` (display name in `locale`, the order's
 *   language, so customer emails read it in theirs).
 * - Its `CartLine.product` is the component itself, so per-piece delivery and
 *   the free-shipping threshold (on the DISCOUNTED subtotal) work unchanged.
 */
export function priceCart(
  products: Product[],
  inputs: CartRequestLine[],
  reservedByOthers?: Map<string, number>,
  now: number = Date.now(),
  packs: Pack[] = [],
  locale: Locale = 'es',
): CartPricingResult {
  const byId = new Map(products.map((p) => [p.id, p]));
  const packsById = new Map(packs.map((p) => [p.id, p]));
  const lines: CartLine[] = [];
  const items: OrderItemSnapshot[] = [];
  let subtotalCents = 0;
  let taxCents = 0;
  let requiresQuote = false;
  let currency = 'EUR';

  // Issues in discovery order, tagged with their submitted line (group) so the
  // backorder warning of a pack held back can be dropped at the end.
  const found: { issue: CartIssue; group: number }[] = [];
  // Submitted lines that will not be priced (any blocking issue in them).
  const blockedGroups = new Set<number>();

  // 1. Expand pack lines into component entries.
  const entries: Entry[] = [];
  inputs.forEach((line, group) => {
    if (!isPackLine(line)) {
      entries.push({ input: line, group });
      return;
    }
    const pack = packsById.get(line.packId);
    if (!pack || pack.status !== 'active' || pack.items.length === 0) {
      blockedGroups.add(group);
      found.push({
        group,
        issue: {
          productId: '',
          packId: line.packId,
          code: 'not_purchasable',
          message: 'Este pack no está disponible actualmente.',
        },
      });
      return;
    }
    const name = packName(pack, locale);
    for (const item of pack.items) {
      const size = packSize(line, item.productId);
      entries.push({
        group,
        input: {
          productId: item.productId,
          quantity: item.quantity * line.quantity,
          ...(size ? { size } : {}),
        },
        pack: { pack, name, packs: line.quantity },
      });
    }
  });

  /** `issue` as reported for `e`: a pack component's carries its packId. */
  const owned = (issue: CartIssue, e: Entry): CartIssue =>
    e.pack ? { ...issue, packId: e.pack.pack.id } : issue;

  // 2. Line-level checks. A pack with any failing component is held back whole.
  const checked = entries.map((e) => {
    const c = checkLine(byId, e.input, now);
    // A pack component without a price («Precio por confirmar») cannot be sold
    // inside a pack (its share would be 0); packAvailability reports `no_price`.
    if (e.pack && !c.issue && c.product.priceCents <= 0) {
      return {
        product: c.product,
        issue: {
          productId: c.product.id,
          code: 'not_purchasable' as const,
          message: 'Este producto no está disponible actualmente.',
        },
      };
    }
    return c;
  });
  checked.forEach((c, i) => {
    if (c.issue) blockedGroups.add(entries[i]!.group);
  });
  const lineBlocked = new Set(blockedGroups);

  // Units requested per product and per line key, over the lines that pass the
  // line-level checks (those are the ones that would be priced).
  const wantedByProduct = new Map<string, number>();
  const wantedByKey = new Map<string, number>();
  checked.forEach((c, i) => {
    if (c.issue || lineBlocked.has(entries[i]!.group)) return;
    const q = entries[i]!.input.quantity;
    const key = lineKey(c.product.id, c.size);
    wantedByProduct.set(c.product.id, (wantedByProduct.get(c.product.id) ?? 0) + q);
    wantedByKey.set(key, (wantedByKey.get(key) ?? 0) + q);
  });
  // Stock lots already found short (product id or line key → its issue, without
  // packId), and the (lot, owner) pairs already reported: each lot is reported
  // once per owner (loose lines = '', or a pack id), and every line drawing on
  // it is held back.
  const blockedLots = new Map<string, CartIssue>();
  const reported = new Set<string>();
  const flaggedBackorder = new Set<string>();
  const priceable: { e: Entry; product: Product; size: string | undefined }[] = [];

  // 3. Stock checks, in submitted order.
  checked.forEach((c, i) => {
    const e = entries[i]!;
    const input = e.input;
    if (c.product) currency = c.product.currency;
    if (c.issue) {
      if (e.pack) {
        const issue: CartIssue =
          c.issue.code === 'enquiry_only'
            ? {
                ...c.issue,
                code: 'not_purchasable',
                message: 'Este producto no está disponible actualmente.',
              }
            : c.issue;
        found.push({ group: e.group, issue: owned(issue, e) });
      } else {
        if (c.issue.code === 'enquiry_only') requiresQuote = true;
        found.push({ group: e.group, issue: c.issue });
      }
      return;
    }
    // Another component of this pack failed its line checks: nothing to stock-check.
    if (lineBlocked.has(e.group)) return;
    const { product, size } = c;
    const key = lineKey(product.id, size);
    const owner = e.pack?.pack.id ?? '';

    const heldLot = blockedLots.has(product.id) ? product.id : blockedLots.has(key) ? key : null;
    if (heldLot !== null) {
      blockedGroups.add(e.group);
      if (!reported.has(`${heldLot}|${owner}`)) {
        reported.add(`${heldLot}|${owner}`);
        found.push({ group: e.group, issue: owned(blockedLots.get(heldLot)!, e) });
      }
      return;
    }
    const block = (lot: string, issue: CartIssue) => {
      blockedLots.set(lot, issue);
      reported.add(`${lot}|${owner}`);
      blockedGroups.add(e.group);
      found.push({ group: e.group, issue: owned(issue, e) });
    };

    // Availability is stock-based for EVERY product — there is no "untracked /
    // unlimited" mode (see the /admin stock model: a piece either tracks stock and
    // hides at 0, or is reasortimiento and sells at 0 with a notice). `available`
    // is raw stock (quote) or raw minus units reserved by OTHER carts (checkout,
    // strict — no floor). Pre-order pieces sell even at 0; a short pre-order line
    // is flagged `backorder` (non-blocking) only in the checkout context.
    const available = reservedByOthers
      ? Math.max(0, product.stockQuantity - reservedForProduct(reservedByOthers, product.id))
      : product.stockQuantity;
    const wanted = wantedByProduct.get(product.id) ?? input.quantity;
    // Per-size lot: only for a sized line whose product tracks units per size.
    const rawSize = size ? sizeUnits(product, size) : null;
    const sizeAvailable =
      rawSize === null
        ? null
        : reservedByOthers
          ? Math.max(0, rawSize - (reservedByOthers.get(key) ?? 0))
          : rawSize;
    const sizeWanted = wantedByKey.get(key) ?? input.quantity;

    if (!product.metadata.preorder) {
      if (available <= 0) {
        block(product.id, {
          productId: product.id,
          code: 'out_of_stock',
          message: 'Producto agotado.',
        });
        return;
      }
      if (wanted > available) {
        block(product.id, {
          productId: product.id,
          code: 'insufficient_stock',
          message: `Solo quedan ${available} unidades.`,
        });
        return;
      }
      if (sizeAvailable !== null && size) {
        if (sizeAvailable <= 0) {
          block(key, {
            productId: product.id,
            size,
            code: 'out_of_stock',
            message: `Talla ${size} agotada.`,
          });
          return;
        }
        if (sizeWanted > sizeAvailable) {
          block(key, {
            productId: product.id,
            size,
            code: 'insufficient_stock',
            message: `Solo quedan ${sizeAvailable} unidades de la talla ${size}.`,
          });
          return;
        }
      }
    } else if (reservedByOthers) {
      // Short pre-order at checkout: still sell it (no `return`), but signal
      // the delay so the buyer can continue or remove it. A size short on its
      // own units is flagged for that size; a shortage of the product total
      // (sizes OK) is flagged once for the product, without a size.
      const sizeShort = sizeAvailable !== null && sizeAvailable < sizeWanted;
      const flagKey = `${sizeShort ? key : product.id}|${owner}`;
      if ((sizeShort || available < wanted) && !flaggedBackorder.has(flagKey)) {
        flaggedBackorder.add(flagKey);
        found.push({
          group: e.group,
          issue: owned(
            {
              productId: product.id,
              ...(sizeShort && size ? { size } : {}),
              code: 'backorder',
              message: 'En reasortimiento: entrega con demora (1–2 semanas aprox.).',
            },
            e,
          ),
        });
      }
    }
    priceable.push({ e, product, size });
  });

  // 4. Price the lines of every submitted line that is not held back. Per pack
  // line: ONE pack's price spread over its components (product id → cents).
  const shares = new Map<number, Map<string, number>>();
  for (const { e, product, size } of priceable) {
    if (blockedGroups.has(e.group)) continue;
    const { input } = e;
    let lineSubtotalCents: number;
    if (e.pack) {
      let share = shares.get(e.group);
      if (!share) {
        const { pack } = e.pack;
        share = allocatePackDiscount(
          pack.items.map((it) => ({
            key: it.productId,
            lineCents: multiplyCents(byId.get(it.productId)?.priceCents ?? 0, it.quantity),
          })),
          packPricing(pack, products).priceCents,
        );
        shares.set(e.group, share);
      }
      lineSubtotalCents = multiplyCents(share.get(product.id) ?? 0, e.pack.packs);
    } else {
      lineSubtotalCents = multiplyCents(product.priceCents, input.quantity);
    }
    const rate = resolveVatRate(product.vatRate, {
      destinationCountry: 'ES',
    });
    const lineTaxCents = vatFromGross(lineSubtotalCents, rate);

    subtotalCents = addCents(subtotalCents, lineSubtotalCents);
    taxCents = addCents(taxCents, lineTaxCents);

    lines.push({ product, quantity: input.quantity, lineSubtotalCents });
    items.push({
      productId: product.id,
      sku: product.sku,
      productName: product.name,
      size: size ?? null,
      quantity: input.quantity,
      // Pack component: its discounted share per unit, informational only.
      unitPriceCents: e.pack ? Math.round(lineSubtotalCents / input.quantity) : product.priceCents,
      vatRate: rate,
      taxCents: lineTaxCents,
      lineTotalCents: lineSubtotalCents,
      ...(e.pack ? { packId: e.pack.pack.id, packName: e.pack.name } : {}),
    });
  }

  const issues = found
    .filter((f) => !(f.issue.code === 'backorder' && blockedGroups.has(f.group)))
    .map((f) => f.issue);
  // `backorder` is informational (the line is priced), so it does not block `ok`.
  const blocking = issues.filter((i) => i.code !== 'backorder');
  return {
    ok: blocking.length === 0 && lines.length > 0,
    issues,
    cart: { lines, items, subtotalCents, taxCents, currency, requiresQuote },
  };
}

/**
 * Per-unit delivery of a large piece (`metadata.deliverySpainCents` > 0) to a
 * zone, BEFORE the fuel surcharge: the per-zone override when set, else the
 * Spain base × the zone's tier multiplier. Shared by the totals and the PDP.
 */
export function pieceDeliveryCents(
  meta: Product['metadata'],
  zoneId: string | undefined,
): number {
  const override =
    zoneId === 'europe'
      ? meta.deliveryEuropeCents
      : zoneId === 'international'
        ? meta.deliveryInternationalCents
        : undefined;
  if (override != null && override > 0) return override;
  return roundCents((meta.deliverySpainCents ?? 0) * tierMultiplier(zoneId));
}

/**
 * Combine a priced cart with a shipping destination into final order totals.
 * Prices are VAT-inclusive; taxCents is the embedded VAT (informational).
 */
export function computeOrderTotals(
  cart: PricedCart,
  destinationCountry: string,
  zones?: ShippingZone[],
  { fuelSurchargePercent = FUEL_SURCHARGE_PERCENT }: { fuelSurchargePercent?: number } = {},
): { totals: OrderTotals; shipping: ShippingResult } {
  const surcharge = (cents: number) => applyFuelSurcharge(cents, fuelSurchargePercent);
  const isSpecial = (l: CartLine) =>
    !!(l.product.metadata.deliverySpainCents && l.product.metadata.deliverySpainCents > 0);

  // Free-shipping threshold applies ONLY to standard (beauty/estilo) items, so
  // the subtotal that unlocks free shipping excludes large objects that carry
  // their own per-piece delivery.
  const standardSubtotal = cart.lines
    .filter((l) => !isSpecial(l))
    .reduce((s, l) => s + l.lineSubtotalCents, 0);
  const hasStandardItems = cart.lines.some((l) => !isSpecial(l));

  const shipping = calculateShipping(
    {
      destinationCountry,
      subtotalCents: standardSubtotal,
      requiresQuote: cart.requiresQuote,
    },
    zones,
  );

  // Per-unit delivery for pieces with their own carrier cost (SDP = base Spain
  // price). By default the destination tier multiplier applies: ES ×1, Europe
  // ×1.5, International ×2.4. A piece MAY override the per-zone amount directly
  // (metadata.deliveryEuropeCents / deliveryInternationalCents) when its real
  // carrier price doesn't follow those ratios (e.g. NAOR teddys 50/70/150).
  // Always charged, ignoring any free-shipping threshold. The temporary fuel
  // surcharge applies per unit (each piece rounds to its own x,95).
  const zone = findZone(destinationCountry, zones);
  let perItemBase = 0;
  let perItemDelivery = 0;
  for (const l of cart.lines) {
    if (!isSpecial(l)) continue;
    const perUnit = pieceDeliveryCents(l.product.metadata, zone?.id);
    perItemBase += perUnit * l.quantity;
    perItemDelivery += surcharge(perUnit) * l.quantity;
  }

  // Zone base applies only when there are standard items; if the cart is all
  // special-delivery pieces, we don't stack the standard zone rate on top.
  // Free shipping (0) is never surcharged.
  const baseShipping =
    shipping.kind === 'priced' && hasStandardItems ? shipping.shippingCents : 0;
  const shippingCents = addCents(surcharge(baseShipping), perItemDelivery);
  const shippingSurchargeCents = shippingCents - baseShipping - perItemBase;

  // Shipping is VAT-inclusive too (accessory to the sale of goods → taxed at the
  // goods' rate). VAT is shown on the FULL total (subtotal + shipping). Uses the
  // standard rate; if reduced-rate goods are added later, apportion here.
  const shippingTaxCents = vatFromGross(shippingCents, DEFAULT_VAT_RATE);

  return {
    shipping,
    totals: {
      subtotalCents: cart.subtotalCents,
      shippingCents,
      ...(shippingSurchargeCents > 0 ? { shippingSurchargeCents } : {}),
      taxCents: addCents(cart.taxCents, shippingTaxCents),
      totalCents: addCents(cart.subtotalCents, shippingCents),
      currency: cart.currency,
    },
  };
}
